import React from 'react';
import { GridColDef } from '@mui/x-data-grid-premium';

// Interface for clipboard paste event parameters
interface ClipboardPasteParams {
  data: string[][];
}

// Interface for clipboard copy event parameters
// Based on the usage pattern in the component
interface ClipboardCopyParams {
  data: string;
}

interface UseDataGridEnhancerProps {
  columns?: GridColDef[];
  enableClipboard?: boolean;
  onClipboardCopy?: (value: string) => void;
  onClipboardPaste?: (value: string) => void;
}

export const useDataGridEnhancer = ({
  columns,
  enableClipboard = true,
  onClipboardCopy,
  onClipboardPaste,
}: UseDataGridEnhancerProps) => {
  const handleBeforeClipboardPasteStart = React.useCallback(
    (params: ClipboardPasteParams) => {
      // Call the custom paste handler if provided
      if (onClipboardPaste) {
        onClipboardPaste(params.data);
      }

      // Allow the paste operation to continue
      return Promise.resolve();
    },
    [onClipboardPaste]
  );

  const handleBeforeClipboardCopyStart = React.useCallback(
    (params: ClipboardCopyParams) => {
      // Call the custom copy handler if provided
      if (onClipboardCopy) {
        onClipboardCopy(params.data);
      }

      // Allow the copy operation to continue with the displayed value
      return Promise.resolve();
    },
    [onClipboardCopy]
  );

  const clipboardProps = enableClipboard
    ? {
        cellSelection: true,
        ignoreValueFormatterDuringExport: false, // Use formatter during copy to get displayed value
        onBeforeClipboardPasteStart: handleBeforeClipboardPasteStart,
        onBeforeClipboardCopyStart: handleBeforeClipboardCopyStart,
      }
    : {
        cellSelection: false,
        disableClipboardPaste: true,
      };

  return {
    enhancedColumns: columns,
    clipboardProps,
  };
};
